import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { APP_VERSION_LABEL, AUTHOR } from '../version'
import { useAuth } from '../state/AuthContext'
import { HelpDialog } from './HelpDialog'
import { Icon } from './Icon'
import { SyncBadge } from './SyncBadge'

type Theme = 'light' | 'dark'

function initialTheme(): Theme {
  try {
    return localStorage.getItem('investment-course.theme') === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme)
  const [helpOpen, setHelpOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { account } = useAuth()

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem('investment-course.theme', theme)
    } catch {
      // Light mode remains the safe fallback.
    }
  }, [theme])

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-inner">
          <Link className="brand" to="/" aria-label="Investment Course home">
            <span className="brand-mark" aria-hidden="true"><span /><span /><span /></span>
            <span className="brand-copy"><strong>Investment Course</strong><small>{APP_VERSION_LABEL}</small></span>
          </Link>
          <button className="icon-button mobile-menu" type="button" onClick={() => setMobileOpen((value) => !value)} aria-label="Open navigation" aria-expanded={mobileOpen}><Icon name="menu" /></button>
          <nav className={`main-nav${mobileOpen ? ' open' : ''}`} aria-label="Main navigation" onClick={() => setMobileOpen(false)}>
            <NavLink to="/"><Icon name="home" size={16} />Home</NavLink>
            <NavLink to="/courses/investing-essentials"><Icon name="book" size={16} />Courses</NavLink>
            <NavLink to="/search"><Icon name="search" size={16} />Search</NavLink>
          </nav>
          <div className="header-actions">
            <SyncBadge />
            <Link className="button ai-button" to="/ask"><Icon name="ai" size={17} />Ask AI</Link>
            <Link className="icon-button" to="/account" aria-label={account ? 'Account settings' : 'Sign in'}><Icon name="user" /></Link>
            <button className="icon-button" type="button" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')} aria-label={theme === 'light' ? 'Use dark mode' : 'Use light mode'}>
              <Icon name={theme === 'light' ? 'moon' : 'sun'} />
            </button>
            <button className="icon-button" type="button" onClick={() => setHelpOpen(true)} aria-label="Open help"><Icon name="help" /></button>
          </div>
        </div>
      </header>
      <main className="app-main">{children}</main>
      <footer className="app-footer">
        <div><strong>{AUTHOR}</strong> · Investment Course · {APP_VERSION_LABEL}</div>
        <div>Educational material, not individualized financial, investment, tax, legal, or accounting advice.</div>
      </footer>
      <aside className="app-global-disclaimer" aria-label="Financial education disclaimer">
        <strong>{AUTHOR} is not a financial advisor.</strong> This is educational material, not individualized financial, investment, tax, legal, or accounting advice.
      </aside>
      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}
